import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BuildIcon from '@mui/icons-material/Build';
import ConstructionIcon from '@mui/icons-material/Construction';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import EventIcon from '@mui/icons-material/Event';
import NoteIcon from '@mui/icons-material/Note';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

// Map event types to icons and colors
const eventConfig = {
  created: { icon: AddIcon, color: 'success' },
  state_changed: { icon: SwapHorizIcon, color: 'info' },
  part_added: { icon: BuildIcon, color: 'success' },
  part_updated: { icon: BuildIcon, color: 'info' },
  part_removed: { icon: BuildIcon, color: 'error' },
  job_added: { icon: ConstructionIcon, color: 'success' },
  job_updated: { icon: ConstructionIcon, color: 'info' },
  job_removed: { icon: ConstructionIcon, color: 'error' },
  job_status_changed: { icon: CheckCircleIcon, color: 'success' },
  customer_set: { icon: PersonIcon, color: 'success' },
  customer_removed: { icon: PersonIcon, color: 'error' },
  employee_assigned: { icon: GroupsIcon, color: 'success' },
  employee_removed: { icon: GroupsIcon, color: 'error' },
  job_employee_assigned: { icon: GroupsIcon, color: 'success' },
  job_employee_removed: { icon: GroupsIcon, color: 'error' },
  scheduled: { icon: EventIcon, color: 'success' },
  rescheduled: { icon: EventIcon, color: 'info' },
  unscheduled: { icon: EventIcon, color: 'error' },
  note_added: { icon: NoteIcon, color: 'success' },
  note_updated: { icon: NoteIcon, color: 'info' },
  note_removed: { icon: NoteIcon, color: 'error' },
  extra_work_requested: { icon: QuestionAnswerIcon, color: 'warning' },
  extra_work_approved: { icon: CheckCircleIcon, color: 'success' },
  extra_work_denied: { icon: CancelIcon, color: 'error' },
  confirmation_requested: { icon: QuestionAnswerIcon, color: 'warning' },
  confirmation_confirmed: { icon: CheckCircleIcon, color: 'success' },
  confirmation_declined: { icon: CancelIcon, color: 'error' },
  confirmation_invalidated: { icon: QuestionAnswerIcon, color: 'default' },
  email_sent: { icon: EmailIcon, color: 'info' },
  sms_sent: { icon: SmsIcon, color: 'info' },
  vehicle_updated: { icon: DirectionsCarIcon, color: 'info' },
  offer_sent: { icon: LocalOfferIcon, color: 'info' },
  invoice_created: { icon: ReceiptIcon, color: 'success' },
  invoice_sent: { icon: ReceiptIcon, color: 'info' },
  invoice_paid: { icon: ReceiptIcon, color: 'success' },
  invoice_refunded: { icon: ReceiptIcon, color: 'warning' },
  invoice_cancelled: { icon: ReceiptIcon, color: 'error' },
  parts_order_status_changed: { icon: LocalShippingIcon, color: 'info' },
};

function EventLogDialog({ open, handleClose, maintenance }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && maintenance?.id) {
      fetchEvents();
    }
  }, [open, maintenance?.id]);

  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/maintenances/${maintenance.id}/event_log/`);
      setEvents(response.data);
    } catch (err) {
      setError(err.response?.data?.error || t('Error loading event log'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'fi' ? 'fi-FI' : 'en-US';
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Group events by date
  const groupEventsByDate = (events) => {
    const groups = {};
    events.forEach((event) => {
      const date = new Date(event.created_at);
      const locale = i18n.language === 'fi' ? 'fi-FI' : 'en-US';
      const dateKey = date.toLocaleDateString(locale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(event);
    });
    return groups;
  };

  const getEventIcon = (eventType) => {
    const config = eventConfig[eventType] || { icon: HistoryIcon, color: 'default' };
    return config;
  };

  // Generate translated description from event type and details
  const getEventDescription = (event) => {
    const { event_type, details } = event;

    switch (event_type) {
      case 'created':
        if (details?.vehicle_registration) {
          return t('eventLog.maintenanceCreatedFor', { vehicle: details.vehicle_registration });
        }
        return t('eventLog.maintenanceCreated');

      case 'state_changed':
        if (details?.old_state && details?.new_state) {
          const oldState = t(details.old_state);
          const newState = t(details.new_state);
          // Moved by the "mark as picked up when paid" setting, live or from history
          if (details.source === 'invoice_paid' || details.source === 'invoice_paid_backfill') {
            return t('eventLog.stateChangedAutoPaid', { from: oldState, to: newState });
          }
          return t('eventLog.stateChanged', { from: oldState, to: newState });
        }
        return t('eventLog.stateChangedGeneric');

      case 'part_added':
        if (details?.part_name) {
          const qty = details.quantity || 1;
          const price = details.unit_price ? `${Number(details.unit_price).toFixed(2)} €` : '';
          return t('eventLog.partAdded', { name: details.part_name, quantity: qty, price });
        }
        return t('eventLog.partAddedGeneric');

      case 'part_updated':
        if (details?.part_name) {
          return t('eventLog.partUpdated', { name: details.part_name });
        }
        return t('eventLog.partUpdatedGeneric');

      case 'part_removed':
        if (details?.part_name) {
          return t('eventLog.partRemoved', { name: details.part_name });
        }
        return t('eventLog.partRemovedGeneric');

      case 'job_added':
        if (details?.job_name) {
          const qty = details.quantity || 1;
          const price = details.unit_price ? `${Number(details.unit_price).toFixed(2)} €` : '';
          return t('eventLog.jobAdded', { name: details.job_name, quantity: qty, price });
        }
        return t('eventLog.jobAddedGeneric');

      case 'job_updated':
        if (details?.job_name) {
          return t('eventLog.jobUpdated', { name: details.job_name });
        }
        return t('eventLog.jobUpdatedGeneric');

      case 'job_removed':
        if (details?.job_name) {
          return t('eventLog.jobRemoved', { name: details.job_name });
        }
        return t('eventLog.jobRemovedGeneric');

      case 'job_status_changed':
        if (details?.job_name) {
          const status = details.completed ? t('eventLog.statusCompleted') : t('eventLog.statusNotCompleted');
          return t('eventLog.jobStatusChanged', { name: details.job_name, status });
        }
        return t('eventLog.jobStatusChangedGeneric');

      case 'customer_set':
        if (details?.customer_name) {
          return t('eventLog.customerSet', { name: details.customer_name });
        }
        return t('eventLog.customerSetGeneric');

      case 'customer_removed':
        return t('eventLog.customerRemoved');

      case 'employee_assigned':
        if (details?.employee_name) {
          return t('eventLog.employeeAssigned', { name: details.employee_name });
        }
        return t('eventLog.employeeAssignedGeneric');

      case 'employee_removed':
        if (details?.employee_name) {
          return t('eventLog.employeeRemoved', { name: details.employee_name });
        }
        return t('eventLog.employeeRemovedGeneric');

      case 'scheduled':
        if (details?.start_time) {
          const start = new Date(details.start_time);
          const end = details.end_time ? new Date(details.end_time) : null;
          const locale = i18n.language === 'fi' ? 'fi-FI' : 'en-US';
          const dateStr = start.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
          const startTime = `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`;
          const endTime = end ? `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}` : '';
          const timeStr = endTime ? `${dateStr} ${startTime}-${endTime}` : `${dateStr} ${startTime}`;
          return t('eventLog.scheduled', { time: timeStr });
        }
        return t('eventLog.scheduledGeneric');

      case 'rescheduled':
        if (details?.old_start_time && details?.new_start_time) {
          const locale = i18n.language === 'fi' ? 'fi-FI' : 'en-US';
          const formatTimeRange = (startStr, endStr) => {
            const start = new Date(startStr);
            const end = endStr ? new Date(endStr) : null;
            const dateStr = start.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
            const startTime = `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`;
            const endTime = end ? `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}` : '';
            return endTime ? `${dateStr} ${startTime}-${endTime}` : `${dateStr} ${startTime}`;
          };
          const fromStr = formatTimeRange(details.old_start_time, details.old_end_time);
          const toStr = formatTimeRange(details.new_start_time, details.new_end_time);
          return t('eventLog.rescheduled', { from: fromStr, to: toStr });
        }
        return t('eventLog.rescheduledGeneric');

      case 'unscheduled':
        if (details?.start_time) {
          const start = new Date(details.start_time);
          const end = details.end_time ? new Date(details.end_time) : null;
          const locale = i18n.language === 'fi' ? 'fi-FI' : 'en-US';
          const dateStr = start.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' });
          const startTime = `${start.getHours().toString().padStart(2, '0')}:${start.getMinutes().toString().padStart(2, '0')}`;
          const endTime = end ? `${end.getHours().toString().padStart(2, '0')}:${end.getMinutes().toString().padStart(2, '0')}` : '';
          const timeStr = endTime ? `${dateStr} ${startTime}-${endTime}` : `${dateStr} ${startTime}`;
          return t('eventLog.unscheduledWithTime', { time: timeStr });
        }
        return t('eventLog.unscheduled');

      case 'note_added':
        return t('eventLog.noteAdded');

      case 'note_updated':
        return t('eventLog.noteUpdated');

      case 'note_removed':
        return t('eventLog.noteRemoved');

      case 'extra_work_requested':
        if (details?.items_count) {
          return t('eventLog.extraWorkRequestedCount', { count: details.items_count });
        }
        return t('eventLog.extraWorkRequestedGeneric');

      case 'extra_work_approved':
        return t('eventLog.extraWorkApproved');

      case 'extra_work_denied':
        return t('eventLog.extraWorkDenied');

      case 'confirmation_requested':
        if (details?.quote_version) {
          return t('eventLog.quoteAcceptanceRequested');
        }
        return t('eventLog.confirmationRequested');

      case 'confirmation_confirmed':
        if (details?.quote_version) {
          return t('eventLog.quoteAccepted');
        }
        return t('eventLog.confirmationConfirmed');

      case 'confirmation_declined':
        if (details?.quote_version) {
          return t('eventLog.quoteDeclined');
        }
        return t('eventLog.confirmationDeclined');

      case 'confirmation_invalidated':
        if (details?.quote_version) {
          return t('eventLog.quoteSuperseded');
        }
        return t('eventLog.confirmationInvalidated');

      case 'email_sent':
        if (details?.recipient) {
          return t('eventLog.emailSent', { recipient: details.recipient });
        }
        return t('eventLog.emailSentGeneric');

      case 'sms_sent':
        if (details?.recipient) {
          return t('eventLog.smsSent', { recipient: details.recipient });
        }
        return t('eventLog.smsSentGeneric');

      case 'vehicle_updated':
        return t('eventLog.vehicleUpdated');

      case 'offer_sent':
        if (details?.recipient) {
          return t('eventLog.offerSent', { recipient: details.recipient });
        }
        return t('eventLog.offerSentGeneric');

      case 'invoice_created':
        if (details?.invoice_number && details?.total !== undefined) {
          return t('eventLog.invoiceCreated', {
            number: details.invoice_number,
            total: `${Number(details.total).toFixed(2)} €`
          });
        }
        return t('eventLog.invoiceCreatedGeneric');

      case 'invoice_sent':
        if (details?.invoice_number) {
          if (details?.type === 'verkkolasku') {
            return t('eventLog.eInvoiceSent', { number: details.invoice_number });
          }
          return t('eventLog.invoiceSent', { number: details.invoice_number });
        }
        return t('eventLog.invoiceSentGeneric');

      case 'invoice_paid':
        if (details?.invoice_number) {
          return t('eventLog.invoicePaid', { number: details.invoice_number });
        }
        return t('eventLog.invoicePaidGeneric');

      case 'invoice_refunded':
        if (details?.invoice_number) {
          return t('eventLog.invoiceRefunded', { number: details.invoice_number });
        }
        return t('eventLog.invoiceRefundedGeneric');

      case 'invoice_cancelled':
        if (details?.invoice_number) {
          return t('eventLog.invoiceCancelled', { number: details.invoice_number });
        }
        return t('eventLog.invoiceCancelledGeneric');

      case 'parts_order_status_changed':
        if (details?.new_status) {
          const to = t(`parts_order.${details.new_status}`);
          if (details.old_status) {
            const from = t(`parts_order.${details.old_status}`);
            return t('eventLog.partsOrderStatusChanged', { from, to });
          }
          return t('eventLog.partsOrderStatusChanged', { from: '-', to });
        }
        return t('eventLog.partsOrderStatusChangedGeneric');

      default:
        // Fallback to backend description
        return event.description || t('eventLog.unknownEvent');
    }
  };

  const renderEvent = (event) => {
    const config = getEventIcon(event.event_type);
    const IconComponent = config.icon;

    return (
      <Box
        key={event.id}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          py: 1.5,
          '&:not(:last-child)': {
            borderBottom: 1,
            borderColor: 'divider',
          },
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${config.color}.lighter`,
            flexShrink: 0,
          }}
        >
          <IconComponent
            sx={{
              fontSize: 18,
              color: `${config.color}.main`,
            }}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {getEventDescription(event)}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              {formatDate(event.created_at)}
            </Typography>
            {event.user_name && (
              <>
                <Typography variant="caption" color="text.disabled">
                  •
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {event.user_name}
                </Typography>
              </>
            )}
            {!event.user_name && event.event_type.includes('extra_work') && (
              <>
                <Typography variant="caption" color="text.disabled">
                  •
                </Typography>
                <Chip
                  label={t('Customer')}
                  size="small"
                  sx={{ height: 18, fontSize: '11px' }}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const groupedEvents = groupEventsByDate(events);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: '12px', sm: '16px' },
          m: { xs: 2, sm: 4 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 64px)' },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon color="primary" />
          <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
            {t('Event Log')}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
            {error}
          </Alert>
        )}

        {!loading && !error && events.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">{t('No events')}</Typography>
          </Box>
        )}

        {!loading && !error && events.length > 0 && (
          <Box>
            {Object.entries(groupedEvents).map(([date, dateEvents], index) => (
              <Box key={date}>
                {index > 0 && <Divider sx={{ my: 2 }} />}
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    mb: 1,
                    textTransform: 'uppercase',
                    fontSize: '11px',
                    letterSpacing: '0.5px',
                  }}
                >
                  {date}
                </Typography>
                {dateEvents.map(renderEvent)}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default EventLogDialog;
